import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Edit3, Save, X } from "lucide-react";
import api from '../../api';

export default function ParametraValidations() {
  const [validations, setValidations] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editedNom, setEditedNom] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchValidations();
  }, []);

  const fetchValidations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get("/validations");
      setValidations(res.data.data);
    } catch (err) {
      console.error("Erreur de chargement :", err);
      setError("Impossible de charger les données. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDoubleClick = (validation) => {
    setEditId(validation.id);
    setEditedNom(validation.nom);
  };

  const handleSave = async (id) => {
    try {
      await api.post(`/validations/${id}`, { 
        nom: editedNom 
      });
      await fetchValidations();
      setEditId(null);
    } catch (err) {
      console.error("Erreur de sauvegarde :", err);
      setError("Échec de la mise à jour. Veuillez vérifier vos modifications.");
    }
  };

  const handleCancel = () => {
    setEditId(null);
    setEditedNom("");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-terrecuite-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
          <p>{error}</p>
          <button 
            onClick={fetchValidations}
            className="mt-2 bg-terrecuite-600 hover:bg-terrecuite-700 text-white px-4 py-2 rounded"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-terrecuite-800 mb-2">
          Paramétrage des Validations
        </h2>
        <p className="text-gray-600">
          Gérer les différents types de validation disponibles dans le système.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <table className="w-full">
          <thead className="bg-terrecuite-50">
            <tr>
              <th className="p-4 text-left text-terrecuite-800 font-medium">#</th>
              <th className="p-4 text-left text-terrecuite-800 font-medium">Nom</th>
              <th className="p-4 text-left text-terrecuite-800 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {validations.length > 0 ? (
              validations.map((validation, index) => (
                <motion.tr
                  key={validation.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-t border-gray-100 hover:bg-terrecuite-50 transition-colors"
                >
                  <td className="p-4 text-gray-700">{index + 1}</td>
                  <td
                    className="p-4 cursor-pointer"
                    onDoubleClick={() => handleDoubleClick(validation)}
                  >
                    {editId === validation.id ? (
                      <input
                        type="text"
                        value={editedNom}
                        onChange={(e) => setEditedNom(e.target.value)}
                        className="border border-gray-300 p-2 rounded-lg w-full focus:ring-2 focus:ring-terrecuite-300 focus:border-terrecuite-500 outline-none transition-all"
                        autoFocus
                      />
                    ) : (
                      <span className="text-gray-800">{validation.nom}</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-3">
                      {editId === validation.id ? (
                        <>
                          <button
                            onClick={() => handleSave(validation.id)}
                            className="text-white bg-terrecuite-600 hover:bg-terrecuite-700 p-2 rounded-lg transition-colors"
                            aria-label="Enregistrer"
                          >
                            <Save size={18} />
                          </button>
                          <button
                            onClick={handleCancel}
                            className="text-white bg-gray-500 hover:bg-gray-600 p-2 rounded-lg transition-colors"
                            aria-label="Annuler"
                          >
                            <X size={18} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleDoubleClick(validation)}
                          className="text-terrecuite-600 hover:text-terrecuite-800 p-2 rounded-lg hover:bg-terrecuite-100 transition-colors"
                          aria-label="Modifier"
                        >
                          <Edit3 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="p-4 text-center text-gray-500">
                  Aucune validation trouvée
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}